import api from './api';

/**
 * Service para gerenciamento do Mural de Notícias
 */
const noticiasMuralService = {
  // ==================== VISUALIZAÇÃO PÚBLICA ====================

  /**
   * Listar notícias publicadas (para o mural público)
   * @param {Object} params - Parâmetros de busca
   * @param {number} params.page - Página atual
   * @param {number} params.limit - Itens por página
   * @param {number} params.categoria_id - Filtrar por categoria
   * @param {string} params.search - Termo de busca
   * @param {boolean} params.apenasDestaque - Apenas notícias em destaque
   * @param {boolean} params.apenasNaoLidas - Apenas não lidas
   */
  listar: async (params = {}) => {
    try {
      const response = await api.get('/noticias-mural', { params });
      return response.data;
    } catch (error) {
      console.error('Erro ao listar notícias:', error);
      throw error;
    }
  },

  /**
   * Buscar notícia por ID
   * @param {number} id - ID da notícia
   */
  buscarPorId: async (id) => {
    try {
      const response = await api.get(`/noticias-mural/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar notícia:', error);
      throw error;
    }
  },

  /**
   * Registrar visualização de uma notícia
   * @param {number} id - ID da notícia
   */
  registrarVisualizacao: async (id) => {
    try {
      const response = await api.post(`/noticias-mural/${id}/visualizar`);
      return response.data;
    } catch (error) {
      console.error('Erro ao registrar visualização:', error);
      // Não propagar erro - visualização é silenciosa
      return null;
    }
  },

  /**
   * Buscar dados para widget do dashboard
   */
  getDashboard: async () => {
    try {
      const response = await api.get('/noticias-mural/dashboard');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar dados do dashboard:', error);
      throw error;
    }
  },

  /**
   * Contar notícias não lidas
   */
  contarNaoLidas: async () => {
    try {
      const response = await api.get('/noticias-mural/nao-lidas/count');
      return response.data;
    } catch (error) {
      console.error('Erro ao contar notícias não lidas:', error);
      return { success: true, data: { total: 0 } };
    }
  },

  /**
   * Listar categorias
   */
  listarCategorias: async () => {
    try {
      const response = await api.get('/noticias-mural/categorias');
      return response.data;
    } catch (error) {
      console.error('Erro ao listar categorias:', error);
      throw error;
    }
  },

  // ==================== GERENCIAMENTO (ADMIN/OPERADOR) ====================

  /**
   * Listar todas as notícias para gerenciamento
   * @param {Object} params - Parâmetros de busca
   */
  listarParaGerenciamento: async (params = {}) => {
    try {
      const response = await api.get('/noticias-mural/gerenciar/all', { params });
      return response.data;
    } catch (error) {
      console.error('Erro ao listar notícias para gerenciamento:', error);
      throw error;
    }
  },

  /**
   * Buscar estatísticas
   */
  getEstatisticas: async () => {
    try {
      const response = await api.get('/noticias-mural/gerenciar/estatisticas');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      throw error;
    }
  },

  /**
   * Criar nova notícia
   * @param {Object} dados - Dados da notícia
   */
  criar: async (dados) => {
    try {
      const response = await api.post('/noticias-mural/gerenciar', dados);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar notícia:', error);
      throw error;
    }
  },

  /**
   * Atualizar notícia
   * @param {number} id - ID da notícia
   * @param {Object} dados - Dados para atualização
   */
  atualizar: async (id, dados) => {
    try {
      const response = await api.put(`/noticias-mural/gerenciar/${id}`, dados);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar notícia:', error);
      throw error;
    }
  },

  /**
   * Excluir notícia
   * @param {number} id - ID da notícia
   */
  excluir: async (id) => {
    try {
      const response = await api.delete(`/noticias-mural/gerenciar/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao excluir notícia:', error);
      throw error;
    }
  },

  /**
   * Publicar notícia
   * @param {number} id - ID da notícia
   */
  publicar: async (id) => {
    try {
      const response = await api.put(`/noticias-mural/gerenciar/${id}/publicar`);
      return response.data;
    } catch (error) {
      console.error('Erro ao publicar notícia:', error);
      throw error;
    }
  },

  /**
   * Arquivar notícia
   * @param {number} id - ID da notícia
   */
  arquivar: async (id) => {
    try {
      const response = await api.put(`/noticias-mural/gerenciar/${id}/arquivar`);
      return response.data;
    } catch (error) {
      console.error('Erro ao arquivar notícia:', error);
      throw error;
    }
  },

  // ==================== CATEGORIAS (ADMIN) ====================

  /**
   * Buscar categoria por ID
   * @param {number} id - ID da categoria
   */
  buscarCategoria: async (id) => {
    try {
      const response = await api.get(`/noticias-mural/categorias/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar categoria:', error);
      throw error;
    }
  },

  /**
   * Criar categoria
   * @param {Object} dados - Dados da categoria
   */
  criarCategoria: async (dados) => {
    try {
      const response = await api.post('/noticias-mural/categorias', dados);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar categoria:', error);
      throw error;
    }
  },

  /**
   * Atualizar categoria
   * @param {number} id - ID da categoria
   * @param {Object} dados - Dados para atualização
   */
  atualizarCategoria: async (id, dados) => {
    try {
      const response = await api.put(`/noticias-mural/categorias/${id}`, dados);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar categoria:', error);
      throw error;
    }
  },

  /**
   * Excluir categoria
   * @param {number} id - ID da categoria
   */
  excluirCategoria: async (id) => {
    try {
      const response = await api.delete(`/noticias-mural/categorias/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao excluir categoria:', error);
      throw error;
    }
  },

  // ==================== SEÇÕES DO PORTAL ====================

  /**
   * Listar todas as seções do portal
   */
  listarSecoes: async () => {
    try {
      const response = await api.get('/noticias-mural/secoes');
      return response.data;
    } catch (error) {
      console.error('Erro ao listar seções:', error);
      throw error;
    }
  },

  /**
   * Buscar notícias de uma seção específica
   * @param {string} codigo - Código da seção (ex: 'destaque_hero', 'eventos')
   * @param {number} limite - Limite de notícias
   */
  buscarPorSecao: async (codigo, limite = 10) => {
    try {
      const response = await api.get(`/noticias-mural/secoes/${codigo}/noticias`, {
        params: { limite }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar notícias da seção:', error);
      throw error;
    }
  },

  /**
   * Buscar notícias mais lidas
   * @param {number} limite - Limite de notícias
   */
  buscarMaisLidas: async (limite = 5) => {
    try {
      const response = await api.get('/noticias-mural/mais-lidas', {
        params: { limite }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar mais lidas:', error);
      throw error;
    }
  },

  /**
   * Buscar todas as notícias organizadas por seção para o portal
   * Retorna um objeto com notícias de cada seção
   */
  getPortal: async () => {
    try {
      const response = await api.get('/noticias-mural/portal');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar portal:', error);
      throw error;
    }
  }
};

export default noticiasMuralService;
